import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing } from '../../util/data';
import { richText } from '../../util/richText';

import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import imagePlaceholder from '../../assets/imagePlaceholder.png';
import {
  Icons,
  ImageSlider,
  NamedLink,
  PublicMoorageCheckInModal,
  ResponsiveImage,
} from '../../components';
import { getProductImages, isArrayLength, isInstantBooking } from '../../util/genericHelpers';
import { getEffectiveTransactionProcessAlias } from '../../util/dataExtractors';
import { getMoorageBadgeLabel, isPublicMoorageListing } from '../../util/listingHelpers';
import {
  fetchListingAvailability,
  selectListingAvailability,
} from '../../ducks/listingAvailability.duck';
import { currentUserSelector } from '../../ducks/user.duck';
import { useSelector, useDispatch } from 'react-redux';
import { toggleFavorite, isFavorite } from '../../containers/FavouritePage/FavouritePage.duck';

import css from './ListingCard.module.css';
import { platformOffPercentage } from '../../util/enums';
import { getDamageWaiverFeeAmount } from '../../util/damageWaiver';
import useDamageWaiverConfig from '../../hooks/useDamageWaiverConfig';
import useNearViewport from '../../hooks/useNearViewport';
import { selectIsAuthenticated } from '../../ducks/auth.duck';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routing/routeConfiguration';
import { formatCheckInAge, getActivePublicMoorageCheckIn } from '../../util/publicMoorageCheckIn';
import { getAvailabilityBadgeData } from '../../util/availabilityBadge';

const MIN_LENGTH_FOR_LONG_WORDS = 10;
const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
const FALLBACK_INFO_ONLY_TITLE = 'Public Moorage';
const FALLBACK_LISTING_TITLE = 'Untitled listing';

const getSafeListingTitle = ({ title, publicData }) => {
  const trimmedTitle = typeof title === 'string' ? title.trim() : '';
  if (trimmedTitle) {
    return trimmedTitle;
  }

  const isInfoOnly = Boolean(publicData?.infoOnly);
  return isInfoOnly ? FALLBACK_INFO_ONLY_TITLE : FALLBACK_LISTING_TITLE;
};

const getSafeListingId = listing => {
  const uuid = listing?.id?.uuid;
  if (typeof uuid === 'string' && uuid.length > 0) {
    return uuid;
  }

  const id = listing?.id;
  if (typeof id === 'string' && id.length > 0) {
    return id;
  }

  return null;
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const damageWaiverConfig = useDamageWaiverConfig();
  const { price, publicData, config, intl, listing } = props;
  const { listingType } = publicData || {};
  const validListingTypes =
    config?.listing?.listingTypes || config?.listingTypes?.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);

  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(getEffectiveTransactionProcessAlias(listing));
  const { priceTitle } = priceData(price, config?.currency, intl);

  const platformFee = price?.amount * (platformOffPercentage / 100);
  const formattedPrice =
    price?.amount === 0
      ? '$0.00'
      : `$${Number(
          (price?.amount + getDamageWaiverFeeAmount(damageWaiverConfig) + platformFee) / 100
        ).toFixed(2)}`;

  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" />
        </div>
      ) : null}
    </div>
  );
};

const handleActionKeyDown = (event, handler) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    handler(event);
  }
};

export const ListingSummary = props => {
  const {
    listing,
    currentUser,
    config,
    intl,
    compact = false,
    variant = 'default',
    hidePublicMoorageBadge = false,
    onPublicMoorageCheckInSuccess,
  } = props;
  const dispatch = useDispatch();
  const [summaryRef, nearViewport] = useNearViewport();
  const availabilityRevision = useSelector(state => state.listingAvailability?.revision || 0);
  const history = useHistory();
  const location = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const listingId = getSafeListingId(listing);
  const { title, price, publicData } = listing?.attributes || {};
  const safeTitle = getSafeListingTitle({ title, publicData });
  const isPublicMoorage = isPublicMoorageListing(publicData);
  const publicMoorageBadgeLabel = getMoorageBadgeLabel(publicData);
  const isInfoOnly = Boolean(publicData?.infoOnly);
  const isSearchGridVariant = variant === 'searchGrid';
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const instantBooking = isInstantBooking(listing);
  const privateActionLabel = instantBooking ? 'Book Now' : 'Request to Book';
  const activeCheckIn = getActivePublicMoorageCheckIn(listing, currentUser);
  const checkInAge = formatCheckInAge(activeCheckIn);

  const availabilityState = useSelector(state =>
    listingId ? selectListingAvailability(state, listingId) : null
  );

  useEffect(() => {
    if (nearViewport && !isPublicMoorage && !isInfoOnly && isBookable && listingId) {
      dispatch(fetchListingAvailability(listing));
    }
  }, [dispatch, listingId, isPublicMoorage, isInfoOnly, isBookable, listing, nearViewport, availabilityRevision]);

  const [timeTick, setTimeTick] = useState(0);

  useEffect(() => {
    if (!isPublicMoorage || !activeCheckIn) return undefined;
    const intervalId = setInterval(() => {
      setTimeTick(tick => tick + 1);
    }, 60 * 1000);
    return () => clearInterval(intervalId);
  }, [isPublicMoorage, activeCheckIn?.checkedInAt]);

  const availabilityBadgeData = useMemo(() => {
    if (isPublicMoorage) {
      return null;
    }
    const availabilityData = availabilityState?.data;
    const availabilityStatus = availabilityState?.status;
    if (!availabilityData || availabilityStatus !== 'loaded') {
      return null;
    }
    return getAvailabilityBadgeData({
      listing,
      availabilityData,
      config,
      intl,
    });
  }, [isPublicMoorage, availabilityState, listing, config, intl]);

  const availabilityLabel = useMemo(() => {
    if (isPublicMoorage) {
      if (activeCheckIn) {
        const age = checkInAge;
        if (age) {
          return `Occupied - updated ${age} ago`;
        }
        return 'Occupied - updated 0 min ago';
      }
      return 'Available to check-in';
    }

    return availabilityBadgeData?.label || 'Check availability';
  }, [isPublicMoorage, activeCheckIn, checkInAge, availabilityBadgeData, timeTick]);

  const availabilityTone = useMemo(() => {
    if (isPublicMoorage) {
      return activeCheckIn ? 'occupied' : 'available';
    }
    if (!availabilityBadgeData) {
      return null;
    }
    return availabilityBadgeData.isBookableToday ? 'available' : 'soon';
  }, [isPublicMoorage, activeCheckIn, availabilityBadgeData]);

  const availabilityContent =
    isPublicMoorage || !isInfoOnly ? (
      <div
        className={classNames(css.availability, {
          [css.availabilityAvailable]: availabilityTone === 'available',
          [css.availabilityOccupied]: availabilityTone === 'occupied',
          [css.availabilitySoon]: availabilityTone === 'soon',
        })}
      >
        <span className={css.availabilityDot} />
        <span>{availabilityLabel}</span>
      </div>
    ) : null;

  const metaPrimaryContent = isPublicMoorage ? (
    !hidePublicMoorageBadge ? (
      <div className={css.publicMoorageBadge}>{publicMoorageBadgeLabel}</div>
    ) : (
      <div className={css.publicMooragePrimaryText}>Public Moorage</div>
    )
  ) : (
    <PriceMaybe
      listing={listing}
      price={price}
      publicData={publicData}
      config={config}
      intl={intl}
    />
  );

  const metaSecondaryContent = isPublicMoorage ? availabilityContent : availabilityContent;

  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [checkInModalMode, setCheckInModalMode] = useState('form');

  const openCheckInModal = mode => {
    setCheckInModalMode(mode);
    setCheckInModalOpen(true);
  };

  const closeCheckInModal = () => setCheckInModalOpen(false);

  const redirectToLogin = () => {
    const from = `${location.pathname}${location.search}`;
    const loginPath = createResourceLocatorString('LoginPage', routeConfiguration(), {}, {});
    history.push({ pathname: loginPath, state: { from } });
  };

  const handleCheckIn = event => {
    event.preventDefault();
    event.stopPropagation();
    if (!isAuthenticated) {
      redirectToLogin();
      return;
    }

    openCheckInModal('form');
  };

  const handleBookNow = event => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!listingId) {
      return;
    }
    const slug = createSlug(safeTitle);
    history.push(
      createResourceLocatorString('ListingPage', routeConfiguration(), { id: listingId, slug }, {})
    );
  };

  return (
    <div
      className={classNames(css.summary, {
        [css.summaryCompact]: compact,
        [css.summaryGrid]: isSearchGridVariant,
      })}
      ref={summaryRef}
      role="button"
      onClick={handleBookNow}
    >
      <div className={css.title}>
        {richText(safeTitle, {
          longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
          longWordClass: css.longWord,
        })}
      </div>

      <div
        className={classNames(css.metaPrimary, {
          [css.metaPrimaryGrid]: isSearchGridVariant,
        })}
      >
        {metaPrimaryContent || (isSearchGridVariant ? <span className={css.metaSpacer} /> : null)}
      </div>

      <div
        className={classNames(css.metaSecondary, {
          [css.metaSecondaryGrid]: isSearchGridVariant,
        })}
      >
        {metaSecondaryContent || (isSearchGridVariant ? <span className={css.metaSpacer} /> : null)}
      </div>

      <div
        className={classNames(css.actionArea, {
          [css.actionAreaGrid]: isSearchGridVariant,
        })}
      >
        {isPublicMoorage ? (
          <div className={css.publicAction}>
            <div
              role="button"
              tabIndex={0}
              className={classNames(css.actionButton, {
                [css.secondaryActionButton]: !!activeCheckIn,
                [css.primaryActionButton]: !activeCheckIn,
                [css.publicPrimaryActionButton]: !activeCheckIn,
              })}
              aria-label={activeCheckIn ? 'View Details' : 'Check In'}
              onClick={activeCheckIn ? handleBookNow : handleCheckIn}
              onKeyDown={e => handleActionKeyDown(e, activeCheckIn ? handleBookNow : handleCheckIn)}
            >
              {activeCheckIn ? 'View Details' : 'Check In'}
            </div>
            {activeCheckIn && !isSearchGridVariant ? (
              <div className={css.actionSubtitle}>May have space for additional boats</div>
            ) : null}
          </div>
        ) : (
          <div
            role="button"
            tabIndex={0}
            className={classNames(css.actionButton, css.primaryActionButton, {
              [css.requestActionButton]: !instantBooking,
            })}
            aria-label={privateActionLabel}
            onClick={handleBookNow}
            onKeyDown={e => handleActionKeyDown(e, handleBookNow)}
          >
            {privateActionLabel}
          </div>
        )}
      </div>

      {isPublicMoorage ? (
        <PublicMoorageCheckInModal
          id={`PublicMoorageCheckInModal-${listingId || 'listing'}`}
          isOpen={checkInModalOpen}
          onClose={closeCheckInModal}
          listing={listing}
          mode={checkInModalMode}
          checkIn={activeCheckIn}
          onCheckInSuccess={onPublicMoorageCheckInSuccess}
        />
      ) : null}
    </div>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const configFromContext = useConfiguration();
  const intl = useIntl();
  const {
    className,
    rootClassName,
    listing,
    setActiveListing,
    isMapVariant,
    variant = 'default',
    disableNamedLink = false,
    config: configFromProps,
    onPublicMoorageCheckInSuccess,
  } = props;
  const config = configFromProps || configFromContext;
  const dispatch = useDispatch();

  const currentListing = ensureListing(listing);
  const listingId = getSafeListingId(currentListing);
  const { title, publicData } = currentListing.attributes || {};
  const safeTitle = getSafeListingTitle({ title, publicData });
  const slug = createSlug(safeTitle);
  const isInfoOnly = Boolean(publicData?.infoOnly);
  const isMapPopupVariant = variant === 'mapPopup';
  const isSearchGridVariant = variant === 'searchGrid';
  const publicMoorageBadgeLabel = getMoorageBadgeLabel(publicData);
  const classes = classNames(rootClassName || css.root, className, {
    [css.mapPopupRoot]: isMapPopupVariant,
    [css.searchGridRoot]: isSearchGridVariant,
  });

  const history = useHistory();
  const currentUser = useSelector(currentUserSelector);
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const isListingFavorite = useSelector(state =>
    listingId ? isFavorite(state, listingId) : false
  );

  const handleFavoriteClick = e => {
    e.preventDefault();
    e.stopPropagation();
    if (!listingId) {
      return;
    }
    if (isAuthenticated) {
      dispatch(toggleFavorite(listingId));
    } else {
      history.push(createResourceLocatorString('FavouritePage', routeConfiguration(), {}, {}));
    }
  };

  const images = getProductImages(currentListing?.images);
  const hasListingImages = isArrayLength(images);
  const primaryImageUrl = hasListingImages ? images[0] : null;
  const shouldUseMapPopupInfoOnlyMedia = isMapPopupVariant && isInfoOnly;
  const [mapPopupImageLoadFailed, setMapPopupImageLoadFailed] = useState(false);
  const shouldUseMapPopupPlaceholder = !primaryImageUrl || mapPopupImageLoadFailed;

  useEffect(() => {
    setMapPopupImageLoadFailed(false);
  }, [listingId]);

  const handleBookNow = () => {
    if (!listingId) {
      return;
    }
    const slug = createSlug(safeTitle);
    history.push(
      createResourceLocatorString('ListingPage', routeConfiguration(), { id: listingId, slug }, {})
    );
  };

  const content = (
    <>
      <div
        className={classNames(css.imageContainer, {
          [css.mapPopupImageContainer]: shouldUseMapPopupInfoOnlyMedia,
        })}
      >
        {shouldUseMapPopupInfoOnlyMedia ? (
          <div className={classNames(css.placeholderMediaContainer, css.mapPopupMediaContainer)}>
            {shouldUseMapPopupPlaceholder ? (
              <>
                <img
                  src={imagePlaceholder}
                  alt="Photos coming soon"
                  className={css.mapPopupMedia}
                  onError={e => {
                    if (e.currentTarget.src !== imagePlaceholder) {
                      e.currentTarget.src = imagePlaceholder;
                    }
                  }}
                />
              </>
            ) : (
              <img
                src={primaryImageUrl}
                alt={`${safeTitle} image`}
                className={css.mapPopupMedia}
                onError={() => setMapPopupImageLoadFailed(true)}
              />
            )}
          </div>
        ) : hasListingImages ? (
          <ImageSlider
            images={images}
            isMobile={isMobile}
            imgAlt={''}
            pathParams={{ id: listingId, slug }}
            pushToListingPage={() => {}}
          />
        ) : (
          <div className={css.placeholderMediaContainer}>
            <img
              src={imagePlaceholder}
              alt="Photos coming soon"
              className={classNames(css.placeholderMedia, {
                [css.placeholderMediaMapVariant]: isMapVariant,
                [css.placeholderMediaSearchGrid]: isSearchGridVariant,
              })}
              onError={e => {
                if (e.currentTarget.src !== imagePlaceholder) {
                  e.currentTarget.src = imagePlaceholder;
                }
              }}
            />
          </div>
        )}
        <button
          className={css.favoriteButton}
          onClick={handleFavoriteClick}
          aria-label={isListingFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Icons name={isListingFavorite ? 'heartFilled' : 'heart'} className={css.heartIcon} />
        </button>
        {isInfoOnly && publicMoorageBadgeLabel ? (
          <div className={classNames(css.publicMoorageBadge, css.publicMoorageBadgeOverlay)}>
            {publicMoorageBadgeLabel}
          </div>
        ) : null}
        {!isInfoOnly && isInstantBooking(listing) ? (
          <div className={css.badge}>
            <Icons name="instantBooking" /> <FormattedMessage id="ListingCard.instantBooking" />
          </div>
        ) : null}
      </div>
      <div
        className={classNames(css.info, {
          [css.isInfoOnlyInfo]: isInfoOnly,
          [css.mapPopupInfo]: isMapPopupVariant,
          [css.searchGridInfo]: isSearchGridVariant,
        })}
      >
        <ListingSummary
          listing={currentListing}
          currentUser={currentUser}
          config={config}
          intl={intl}
          compact={isMapPopupVariant}
          variant={variant}
          hidePublicMoorageBadge={isInfoOnly}
          onPublicMoorageCheckInSuccess={onPublicMoorageCheckInSuccess}
        />
      </div>
    </>
  );

  return disableNamedLink ? (
    <div
      className={classes}
      role="button"
      tabIndex={0}
      onClick={handleBookNow}
      onKeyDown={e => handleActionKeyDown(e, handleBookNow)}
    >
      {content}
    </div>
  ) : (
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id: listingId, slug }}
      onClick={handleBookNow}
    >
      {content}
    </NamedLink>
  );
};

export default ListingCard;
